import React from 'react'
import { Button, Col, Container, Form } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import { EmailRegexp, PhoneRegexp, NameRegexp, InvalidNameMessage, MaxDonorIdLength, InvalidDonorIdLength, DonorIdRegexp, InvalidDonorId } from '../../config/Constants';
import FormatZipCode from '../../formatters/FormatZipCode';
import PhoneNumberMaskedInput from '../FormattedInputs/PhoneNumberMaskedInput';
import State from '../../api/OrderRequests/OrderRequestsModel/State';
import { Subscription } from 'rxjs';
import { StateObservable } from '../../api/OrderRequests/OrderRequestsConstraintGet';
import StateSelect from '../FormattedInputs/StateSelect';
import AutoFilterLocation from '../../api/Location/LocationModel/AutoFilterLocation';
import AutoFilterCompany from '../../api/Companies/CompanyModel/AutoFilterCompany';
import AutoFilterRegion from '../../api/Regions/Model/AutoFilterRegion';
import AutoFilterLocationsGet from '../../api/Location/AutoFilterLocationsGet';
import CompanyInput from '../FormattedInputs/CompanyInput';
import GovPoolData from '../../api/Randoms/RandomsModel/GovPoolData';
import GovPoolGet from '../../api/Randoms/GovPoolGet';
import { AppState } from '../../AppState';
import { connect } from 'react-redux';
import { isCompanyAdmin, isLocationAdmin, isPortalAdmin } from "../../authentication/isAdminRole";
import { GetSelectedAssignedClinic } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors";
import DonorRecord from '../../api/Randoms/RandomsModel/DonorRecord';
import { GovDonorAddObservable } from '../../api/GovDonor/GovDonorAddObservable';
import { toast } from 'react-toastify';
import ActionButton from '../ActionButton';
import GridRow from '../../api/GovDonor/GridRow'
import { GovDonorEditObservable } from '../../api/GovDonor/GovDonorEditObservable';
import FormatPhone from '../../formatters/FormatPhone';
import { FaPencilAlt, FaTimes } from "react-icons/fa"
import FormatError from '../../formatters/FormatError';
import FormatDate from '../../formatters/FormatDate';

type StateKey = "WorkPhone" | "MobilePhone"
type StateValidKey = "isWorkPhoneValid" | "isMobilePhoneValid"

interface EditGovDonorProps {
  PersonID?: number
  editing: boolean
  onHide: (hasUpdates: boolean) => void
  onSuccess: () => void
  row?: GridRow
}

interface EditGovDonorAuthProps {
  companyID?: number
  isLocationAdmin?: boolean
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
}

interface DonorDetailsValidity {
  isDonorIdValid: boolean
  isLastNameValid: boolean
  isFirstNameValid: boolean
  isMobilePhoneValid: boolean
  isWorkPhoneValid: boolean
  isGenderValid: boolean
  isGroupAssignmentValid: boolean
  isEmailValid: boolean
}

interface EditGovDonorState extends DonorDetailsValidity {
  HireDate: string
  Gender?: string
  Birthday: string
  FirstName: string
  LastName: string
  DonorId: string
  GroupAssignment: string
  GroupAssignmentName: string
  Zip: string
  City: string
  Address1: string
  WorkPhone: string
  MobilePhone: string
  Email: string
  Active: boolean
  states: State[]
  state?: State
  locations: AutoFilterLocation[]
  selectedLocation?: AutoFilterLocation
  companies: AutoFilterCompany[]
  selectedCompany?: AutoFilterCompany
  govPools: GovPoolData[]
  saving: boolean
  editing: boolean

  isStatesLoading: boolean
  isLocationsLoading: boolean
  isCompaniesLoading: boolean
  isGovPoolsLoading: boolean
}

class EditGovDonorForm extends React.Component<EditGovDonorProps & EditGovDonorAuthProps, EditGovDonorState> {
  subscriptions = new Subscription()
  saveSubscription?: Subscription

  componentDidMount() {
    this.setState({ isStatesLoading: true, isCompaniesLoading: true, isGovPoolsLoading: true })
    this.setState({ editing: this.props.editing })

    this.subscriptions.add(
      GovPoolGet().subscribe(
        (results: GovPoolData[]) => this.setState({ govPools: results, isGovPoolsLoading: false }),
        (error) => {
          this.setState({ isGovPoolsLoading: false })
          toast.error(FormatError(error))
        }
      )
    )

    this.subscriptions.add(
      StateObservable().subscribe(
        (results: State[]) => {
          if (this.props.row?.PersonID) {
            var selectedState = results.find(state => state.Abbreviation === this.props.row?.StateCode);
            if (selectedState !== undefined) {
              this.setState({ state: selectedState })
            }
          }
          this.setState({ states: results, isStatesLoading: false }),
            () => {
              this.setState({ isStatesLoading: false })
            }
        },
      )
    )

    this.loadPersonData()
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
    this.saveSubscription?.unsubscribe()
  }

  loadPersonData() {
    if (!this.props.row || !this.props.PersonID) {
      return
    }
    this.setState({
      HireDate: this.props.row?.HireDate,
      Gender: this.props.row?.Gender,
      Birthday: this.props.row?.DateOfBirth,
      LastName: this.props.row?.LastName!,
      FirstName: this.props.row?.FirstName!,
      DonorId: this.props.row?.EmployerID.toString(),
      GroupAssignment: (this.props.row?.GovPoolID || "").toString(),
      GroupAssignmentName: this.props.row?.GovPoolName,
      Zip: this.props.row?.Zip,
      City: this.props.row?.City,
      Address1: this.props.row?.Address,
      WorkPhone: this.props.row?.WorkPhone,
      MobilePhone: this.props.row?.CellPhone,
      Email: this.props.row?.Email,
      Active: this.props.row?.Active
    })
  }

  performSave = () => {
    const donor: DonorRecord = {
      PersonID: this.props.PersonID,
      FirstName: this.state.FirstName.trim(),
      LastName: this.state.LastName.trim(),
      DonorId: this.state.DonorId.replace(/\s+/g, " ").replace(/[-']/g,""),
      GovPoolId: this.state.GroupAssignment,
      IntakeDate: FormatDate(this.state.HireDate),
      Gender: this.state.Gender,
      Birthday: FormatDate(this.state.Birthday),
      Company: this.state.selectedCompany?.AccountID,
      Location: this.state.selectedLocation?.AccountID,
      Address1: this.state.Address1,
      City: this.state.City,
      State: this.state.state?.StateProvinceTerritoryID,
      WorkPhone: this.state.WorkPhone.replace(/\D/g, ""),
      MobilePhone: this.state.MobilePhone.replace(/\D/g, ""),
      Email: this.state.Email,
      Inactive: !this.state.Active,
      Zip: this.state.Zip,
      isPortalAdmin: this.props.isPortalAdmin
    }
    if (!donor.Company) {
      donor.Company = this.props.companyID
    }
    this.setState({ saving: true })

    this.saveSubscription = ((this.props.PersonID === undefined) ? GovDonorAddObservable(donor) : GovDonorEditObservable(donor)).subscribe(
      data => {
        toast.success(data)
        this.setState({ saving: false })
        this.props.onSuccess();
      },
      error => {
        this.setState({ saving: false })
        toast.error(error?.errorData?.message || error.message)
      }
    )
  }

  loadAllLocations = (company?: AutoFilterCompany, region?: AutoFilterRegion) => {
    this.setState({ isLocationsLoading: true })
    this.subscriptions.add(
      AutoFilterLocationsGet(company?.AccountID || 0, region?.Code || "", "").subscribe(
        data => {
          let location = data.find(l => l.AccountID === this.props.row?.LocationID)
          if (this.props.isLocationAdmin && !this.props.PersonID) {
            location = data[0]
          }
          this.setState({ locations: data, isLocationsLoading: false, selectedLocation: location })
        },
        () => this.setState({ isLocationsLoading: false })
      )
    )
  }

  defaultState: EditGovDonorState = {
    HireDate: "",
    Gender: "",
    Birthday: "",
    FirstName: "",
    LastName: "",
    DonorId: "",
    GroupAssignment: "",
    GroupAssignmentName: "",
    Zip: "",
    City: "",
    Address1: "",
    WorkPhone: "",
    MobilePhone: "",
    Email: "",
    Active: true,
    states: [],
    state: undefined,
    locations: [],
    selectedLocation: undefined,
    companies: [],
    selectedCompany: undefined,
    govPools: [],

    isDonorIdValid: true,
    isLastNameValid: true,
    isFirstNameValid: true,
    isMobilePhoneValid: true,
    isWorkPhoneValid: true,
    isGenderValid: true,
    isGroupAssignmentValid: true,
    isEmailValid: true,

    isStatesLoading: true,
    isLocationsLoading: false,
    isCompaniesLoading: false,
    isGovPoolsLoading: false,
    saving: false,
    editing: false,
  }

  state: EditGovDonorState = { ...this.defaultState }

  handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || ""
    this.setState({ FirstName: value, isFirstNameValid: this.isNameValid(value) })
  }

  handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || ""
    this.setState({ LastName: value, isLastNameValid: this.isNameValid(value) })
  }

  handleDonorIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    this.setState({ DonorId: value, isDonorIdValid: this.isDonorIdValid(value) })
  }
  handleGenderChange = (event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ Gender: event.target.value })
  handleCompanyChange = (selected: AutoFilterCompany) => {
    this.setState({ selectedCompany: selected })
    if (!this.props.isPortalAdmin) {
      this.loadAllLocations(selected, undefined)
    }
  }

  handleLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    let location = this.state.locations.find(l => l.AccountID === Number(event.target.value))
    this.setState({ selectedLocation: location })
  }
  handleStreetAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ Address1: event.target.value })
  handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ City: event.target.value })
  handleStateChange = (state: State) => this.setState({ state: state })
  handleZipChange = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ Zip: FormatZipCode(event.target.value) })
  handleWorkPhoneChange = (value: string, stateKey: StateKey, stateValidKey: StateValidKey) => this.setState({ ...{}, [stateKey]: value, [stateValidKey]: this.isPhoneValid(value) })
  handleMobilePhoneChange = (value: string, stateKey: StateKey, stateValidKey: StateValidKey) => this.setState({ ...{}, [stateKey]: value, [stateValidKey]: this.isPhoneValid(value) })
  handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ Email: event.target.value, isEmailValid: this.emailValid(event.target.value) })
  handleGroupAssignmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ GroupAssignment: event.target.value })
  isNameValid = (value: string) => (value.length > 0 && NameRegexp.test(value))
  emailValid = (value: string) => (value.length === 0) || EmailRegexp.test(value)
  isPhoneValid = (value: string) => (value.length === 0 || value.toLowerCase() === 'n/p' || PhoneRegexp.test(FormatPhone(value)))
  isDonorIdValid = (value: string) => (value.length > 0 && value.length <= MaxDonorIdLength && DonorIdRegexp.test(value))
  handleToggleEditMode = () => this.setState((prevState) => { return { editing: !prevState.editing } })
  isValid = () => {
    return this.isNameValid(this.state.FirstName) && this.isNameValid(this.state.LastName) && this.isDonorIdValid(this.state.DonorId) && !!this.state.Gender &&
      this.isPhoneValid(this.state.WorkPhone) && this.isPhoneValid(this.state.MobilePhone) && this.emailValid(this.state.Email) && !!this.state.Birthday
  }

  render() {
    const editable = this.state.editing ? "form-group required" : "form-group"
    const donorInfoLabel = this.props.PersonID ? "DONOR INFO" : ""
    return <div>
      <Container className="general-info px-4 pb-4 pt-3">
        <Form>
          {this.props.PersonID && <Form.Row className="px-0 pt-2">
            <Col md={12}>
              <Button variant="icon-link" className="float-right ml-1 btn-icon-primary" size="sm" onClick={this.handleToggleEditMode} >
                {!this.state.editing && <FaPencilAlt />}
                {this.state.editing && <FaTimes />}
              </Button>
            </Col>
          </Form.Row>}
          <Form.Row className="form-group">
            <Col sm={5}><h5>{donorInfoLabel}</h5></Col>
            <Col sm={7}>
              <Form.Row>
                <Col sm={6}>
                  <h5>Group Assignment</h5>
                </Col>
                <Col sm={6} className="align-self-center">
                  {!this.state.editing && <Form.Label className="col-form-label">{this.state.GroupAssignmentName}</Form.Label>}
                  {this.state.editing && <Form.Control id="groupAssignment" as="select" value={this.state.GroupAssignment} onChange={this.handleGroupAssignmentChange}>
                    <option value="">Select</option>
                    {this.state.govPools.map(gp => <option value={gp.GovPoolId} key={gp.GovPoolId}>{gp.Name}</option>)}
                  </Form.Control>}
                </Col>
              </Form.Row>
            </Col>
          </Form.Row>
          <Form.Row className={editable}>
            <Form.Label column sm={5} htmlFor="firstName">First Name</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.FirstName}</Form.Label>}
              {this.state.editing && <Form.Control id="firstName" value={this.state.FirstName} type="text" isInvalid={!this.state.isFirstNameValid} onChange={this.handleFirstNameChange} maxLength={30} />}
              {this.state.editing && <Form.Control.Feedback type="invalid">{InvalidNameMessage}</Form.Control.Feedback>}
            </Col>
          </Form.Row>
          <Form.Row className={editable}>
            <Form.Label column sm={5} htmlFor="lastName">Last Name</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.LastName}</Form.Label>}
              {this.state.editing && <Form.Control id="lastName" type="text" value={this.state.LastName} isInvalid={!this.state.isLastNameValid} onChange={this.handleLastNameChange} maxLength={30} />}
              {this.state.editing && <Form.Control.Feedback type="invalid">{InvalidNameMessage}</Form.Control.Feedback>}
            </Col>
          </Form.Row>
          <Form.Row className={editable}>
            <Form.Label column sm={5} htmlFor="donorId">Donor ID</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.DonorId}</Form.Label>}
              {this.state.editing && <Form.Control id="donorId" type="text" value={this.state.DonorId} isInvalid={!this.state.isDonorIdValid} onChange={this.handleDonorIdChange} maxLength={MaxDonorIdLength} />}
              {this.state.editing && <Form.Control.Feedback type="invalid">
                {!DonorIdRegexp.test(this.state.DonorId) ? InvalidDonorId : InvalidDonorIdLength}
              </Form.Control.Feedback>}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="hideDate">Intake Date</Form.Label>
            <Col md={6} className="mb-1 mb-lg-0">
              {!this.state.editing && <Form.Label className="col-form-label">{FormatDate(this.state.HireDate)}</Form.Label>}
              {this.state.editing && <DatePicker className="form-control"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                selected={this.state.HireDate ? new Date(this.state.HireDate) : undefined}
                enableTabLoop={false}
                onChange={date => this.setState({ HireDate: date?.toString() || "" })}
              />}
            </Col>
          </Form.Row>
          <Form.Row className={editable}>
            <Form.Label column sm={5} htmlFor="gender">Gender</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.Gender === "M" ? "Male" : "Female"}</Form.Label>}
              {this.state.editing && <Form.Control id="gender" value={this.state.Gender} as="select" onChange={this.handleGenderChange}>
                <option value="">Select</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </Form.Control>}
            </Col>
          </Form.Row>
          <Form.Row className={editable}>
            <Form.Label column sm={5} htmlFor="birthday">Date of Birth</Form.Label>
            <Col md={6} className="mb-1 mb-lg-0">
              {!this.state.editing && <Form.Label className="col-form-label">{FormatDate(this.state.Birthday)}</Form.Label>}
              {this.state.editing && <DatePicker className="form-control mb-1 mb-lg-0"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                selected={this.state.Birthday ? new Date(this.state.Birthday) : undefined}
                enableTabLoop={false}
                maxDate={new Date()}
                onChange={date => this.setState({ Birthday: date?.toString() || "" })} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="company">Company</Form.Label>
            <Col sm={7}>
              {(!this.props.isPortalAdmin || (this.props.isPortalAdmin && !this.state.editing)) && <Form.Label className="col-form-label">{this.state.selectedCompany?.Name}</Form.Label>}
              {this.state.editing && this.props.isPortalAdmin && <CompanyInput
                filter onChange={items => this.handleCompanyChange(items[0])} placeholder="Choose a company..." />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="location">Location</Form.Label>
            <Col sm={7}>
              {(this.props.isLocationAdmin || ((this.props.isPortalAdmin || this.props.isCompanyAdmin) && !this.state.editing)) && <Form.Label className="col-form-label">{this.state.selectedLocation?.Name}</Form.Label>}
              {(this.props.isPortalAdmin || this.props.isCompanyAdmin) && this.state.editing && <Form.Control id="location" as="select" value={this.state.selectedLocation?.AccountID} onChange={this.handleLocationChange}>
                <option value="">Select</option>
                {this.state.locations.map(l => <option value={l.AccountID} key={l.AccountID}>{l.Name}</option>)}
              </Form.Control>}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="streetAddress">Street Address</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.Address1}</Form.Label>}
              {this.state.editing && <Form.Control id="streetAddress" value={this.state.Address1} type="text" onChange={this.handleStreetAddressChange} maxLength={60} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="city">City</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.City}</Form.Label>}
              {this.state.editing && <Form.Control id="city" type="text" value={this.state.City} onChange={this.handleCityChange} maxLength={40} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="state">State</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.state?.FullName}</Form.Label>}
              {this.state.editing && <StateSelect states={this.state.states} dropButtonIfInvalid selectedCode={this.state.state?.Abbreviation} onChange={(state) => this.handleStateChange(state)} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="zip">Zip</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.Zip}</Form.Label>}
              {this.state.editing && <Form.Control id="zip" type="text" value={this.state.Zip} onChange={this.handleZipChange} maxLength={15} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="workPhone">Work Phone</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{FormatPhone(this.state.WorkPhone)}</Form.Label>}
              {this.state.editing && <PhoneNumberMaskedInput value={this.state.WorkPhone} onChange={(value: string) => this.handleWorkPhoneChange(value, "WorkPhone", "isWorkPhoneValid")} isInvalid={!this.state.isWorkPhoneValid} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="mobilePhone">Mobile Phone</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{FormatPhone(this.state.MobilePhone)}</Form.Label>}
              {this.state.editing && <PhoneNumberMaskedInput value={this.state.MobilePhone} onChange={(value: string) => this.handleMobilePhoneChange(value, "MobilePhone", "isMobilePhoneValid")} isInvalid={!this.state.isMobilePhoneValid} />}
            </Col>
          </Form.Row>
          <Form.Row className="form-group">
            <Form.Label column sm={5} htmlFor="email">Email</Form.Label>
            <Col sm={7}>
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.Email}</Form.Label>}
              {this.state.editing && <Form.Control id="email" value={this.state.Email} isInvalid={!this.state.isEmailValid} type="text" onChange={this.handleEmailChange} maxLength={60} />}
            </Col>
          </Form.Row>
          {this.props.PersonID && <Form.Row className="form-group">
            <Form.Label column sm={5}>ACTIVE</Form.Label>
            <Col sm={7}>
              {this.state.editing && <Form.Check checked={this.state.Active} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ Active: e.target.checked })} className="mt-2" />}
              {!this.state.editing && <Form.Label className="col-form-label">{this.state.Active ? "Yes" : "No"} </Form.Label>}
            </Col>
          </Form.Row>}
          <Form.Row className="buttons-block">
            <Button variant="secondary" onClick={() => this.props.onHide(false)}>CANCEL</Button>
            <ActionButton variant="primary" hidden={!this.state.editing} disabled={!this.isValid()} onClick={this.performSave} spin={this.state.saving} disableOnSpin>SAVE</ActionButton>
          </Form.Row>
        </Form>
      </Container>
    </div>
  }
}

function mapStateToProps(state: AppState): EditGovDonorAuthProps {
  const selectedCompany = GetSelectedAssignedClinic(state)  
  return {
    companyID: selectedCompany?.AccountID || 0,
    isLocationAdmin: isLocationAdmin(state),
    isCompanyAdmin: isCompanyAdmin(state),
    isPortalAdmin: isPortalAdmin(state)
  }
}

const ConnectedEditGovDonor = connect(mapStateToProps, null)(EditGovDonorForm)
export default ConnectedEditGovDonor
